/**
 * @fileoverview QCQuestion component for rendering a QC question
 *
 * @example
 * // Usage in component:
 * import { QCQuestion } from './QC'
 *
 * <QCQuestion onSubmit={onSubmit} question={question} />
 */

import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQCErrors } from '../../hooks/useQCErrors'
import { MiddlewareContext } from '../../MiddlewareContext'
import { NavigationButton } from '../NavigationButton'
import { DesktopProgress, MobileProgress } from '../Progress/Progress'
import { StreamingRecorder, StreamingState } from '../StreamingRecorder/StreamingRecorder'
import { Text } from '../typography/Typography'
import css from './Audio.module.css'
import { Instructions } from './sections/Instructions'
import { Title } from './sections/Title'

/**
 * @function getError
 * @description Gets the error from the errors object
 * @param {Record<string, string>} errors - The errors object
 * @returns {Novoic.ValidationError} The error
 */
function getError(errors: Record<string, string>): Novoic.ValidationError {
    const name = Object.keys(errors)[0]
    const error = errors[name]
    return {
        name,
        error,
    }
}

/**
 * @function getTitle
 * @description Gets the title based on the streaming state
 * @param {Novoic.AudioQuestionBody} body - The body of the question
 * @param {StreamingState} state - The streaming state
 * @returns {string} The title
 */
function getTitle(body: Novoic.AudioQuestionBody, state: StreamingState): string {
    switch (state) {
        case StreamingState.IDLE:
        case StreamingState.LOADING:
            return body.title
        case StreamingState.PLAYING:
            return body.titlePlaying
        case StreamingState.STREAMING:
            return body.titleRecording
        case StreamingState.PROCESSING:
        default:
            return ''
    }
}

/**
 * @function QCQuestion
 * @description Component for rendering a QC question
 * @param {Novoic.AudioQuestionProps} props - The props for the component
 * @returns {JSX.Element} The QCQuestion component
 */
export function QCQuestion({ onSubmit, question }: Novoic.AudioQuestionProps) {
    const { id, question: body } = question
    const {
        instructions,
        subtext,
        audio,
        recordingStartMode,
        recordingStopMode,
        maxRecordingTime,
    } = body

    const [store, retrieve] = useQCErrors(id)
    const { exit } = useContext(MiddlewareContext)
    const [storedAttempt, storedErrors, errorCount] = retrieve()
    const [attempt, setAttempt] = useState<number>(storedAttempt)
    const [state, setState] = useState<StreamingState>(StreamingState.IDLE)
    const [showErrors, setShowErrors] = useState<boolean>(Boolean(storedErrors))
    const { t } = useTranslation()
    const params = new URLSearchParams(window.location.search)

    /**
     * Submit when streaming is finished
     */
    async function onStreamingEnded(recording: string) {
        const errors = await onSubmit(id, { recording, skip: attempt === 2 })
        if (errors) {
            store(attempt, getError(errors as Record<string, string>))
            setState(StreamingState.IDLE)
            setAttempt(attempt + 1)
            setShowErrors(true)
        }
    }

    /**
     * Change the state of the streaming so UI can be updated based on it
     */
    function onStateChange(state: StreamingState) {
        setState(state)
    }

    if (showErrors && storedErrors) {
        const idx = errorCount - 1
        const title = t(`errors.${storedErrors.name}.${idx}.title`)
        const instructions = t(`errors.${storedErrors.name}.${idx}.instructions`)
        const button = t(`errors.${storedErrors.name}.${idx}.button`)

        return (
            <div className={css.container}>
                <Title>{title}</Title>
                <Instructions instructions={instructions} />

                <div className={css.button_container}>
                    <NavigationButton
                        type="confirm"
                        label={button}
                        onClick={() => {
                            setShowErrors(false)
                            return Promise.resolve()
                        }}
                    />
                </div>
            </div>
        )
    }

    return (
        <div className={css.container}>
            <MobileProgress />
            <Title flexible>{getTitle(body, state)}</Title>
            {subtext && <Text>{subtext}</Text>}
            {instructions && <Instructions instructions={instructions} />}

            <StreamingRecorder
                attempt={attempt}
                skippable={params.get('debug') === 'true'}
                onStreamingEnded={onStreamingEnded}
                onStateChange={onStateChange}
                onExit={exit}
                audio={audio}
                recordingStartMode={recordingStartMode}
                recordingStopMode={recordingStopMode}
                maxRecordingTime={maxRecordingTime}
            />

            <div className={css.progress}>
                <DesktopProgress />
            </div>
        </div>
    )
}
